import { useCallback, useEffect, useMemo, useState } from "react";
import { FiKey, FiPlus, FiSearch, FiUsers } from "react-icons/fi";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";
import CreateUserModal from "../../../components/admin/users/CreateUserModal";
import UserBranchAccessModal from "../../../components/admin/users/UserBranchAccessModal";

export default function UsersPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [accessUser, setAccessUser] = useState(null);

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const [usersData, rolesData, branchesData, storesData] =
        await Promise.all([
          apiRequest("/users", { accessToken }),
          apiRequest("/roles", { accessToken }),
          apiRequest("/branches", { accessToken }),
          apiRequest("/stores", { accessToken }),
        ]);

      setUsers(usersData || []);
      setRoles(rolesData || []);
      setBranches(branchesData || []);
      setStores(storesData || []);
    } catch (err) {
      setError(err.message || "Unable to load users.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadData();
  }, [isAuthLoading, accessToken, loadData]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return users;

    return users.filter((user) =>
      [
        user.email,
        user.phone,
        user.employee?.fullName,
        user.employee?.employeeCode,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term)),
    );
  }, [search, users]);

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Administration / Users</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Users & Access
          </h1>

          <p className="mt-2 text-gray-500">
            Create ERP accounts and assign roles. ERP data is visible across all branches according to role permissions.
          </p>
        </div>

        <button
          onClick={() => setCreateOpen(true)}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white"
        >
          <FiPlus />
          Create User
        </button>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-3">
        <Stat label="Total Users" value={users.length} />
        <Stat
          label="Active Users"
          value={users.filter((user) => user.status === "ACTIVE").length}
          color="text-green-600"
        />
        <Stat
          label="Users With Branch Assignments"
          value={
            users.filter((user) =>
              user.branchAccess?.some((access) => access.isActive),
            ).length
          }
          color="text-primary-700"
        />
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="relative w-full md:w-96">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search email, phone, employee name..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700"
            />
          </div>
        </div>

        {error && (
          <div className="m-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                {["User", "Roles", "Branch Assignments", "Status", "Actions"].map(
                  (heading) => (
                    <th
                      key={heading}
                      className="px-6 py-4 text-left text-xs font-medium uppercase text-gray-500"
                    >
                      {heading}
                    </th>
                  ),
                )}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading users…
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const activeBranches =
                    user.branchAccess?.filter((access) => access.isActive) ||
                    [];

                  return (
                    <tr key={user.id} className="border-b border-gray-100">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900">
                          {user.employee?.fullName ||
                            user.email ||
                            user.phone ||
                            "ERP User"}
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          {user.email || user.phone}
                          {user.employee?.employeeCode
                            ? ` · ${user.employee.employeeCode}`
                            : ""}
                        </p>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {user.roles?.map(({ role }) => (
                            <span
                              key={role.code}
                              className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
                            >
                              {role.name}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-700">
                        <p className="font-medium text-primary-800">All branches · role permissions apply</p>
                        <p className="mt-1 text-xs text-gray-500">
                        {activeBranches.length === 0
                          ? "No branch assigned"
                          : activeBranches.map((access) => access.branch.name).join(", ")}
                        </p>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-medium ${
                            user.status === "ACTIVE"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {user.status}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <button
                          onClick={() => setAccessUser(user)}
                          className="flex items-center gap-2 rounded-xl bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100"
                        >
                          <FiKey />
                          Manage Access
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        roles={roles}
        onCreated={loadData}
      />

      <UserBranchAccessModal
        open={Boolean(accessUser)}
        user={accessUser}
        branches={branches}
        stores={stores}
        onClose={() => setAccessUser(null)}
        onChanged={loadData}
      />
    </div>
  );
}

function Stat({ label, value, color = "text-gray-900" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
